import type { AnimationDataV4, IngredientV4 } from './types/v4';
import type { AnimationDataV5, IngredientV5, StageIdV5 } from './types/v5';

/**
 * V4 → V5. Two shape changes land together:
 *
 *  1. Every `Ingredient` gains a required `id` field. The id is a stable
 *     identity that survives renames — `name` is now mutable user-facing
 *     copy while consumers (cards, transform panels, shot-prompt rewrites)
 *     key off `id`. Old saves are upgraded by setting `id = name`. Pre-V5
 *     saves had at most one defined ingredient and derived names come from
 *     the detection LLM (which dedupes its own output), so collisions in a
 *     single blob are extremely unlikely. A defensive `-2`, `-3` suffix
 *     dedupe is applied just in case.
 *
 *  2. `'character'` and `'prepare-style'` stages were merged into a single
 *     unified `'ingredients'` stage, so the `StageId` union drops both old
 *     ids and adds `'ingredients'`. Any saved blob whose `activeStageId`
 *     was one of the dropped ids is coerced to `'ingredients'` so the user
 *     lands on the equivalent merged stage on reload. Other stage ids pass
 *     through unchanged.
 */
export function v4ToV5(input: AnimationDataV4): AnimationDataV5 {
    const ingredients = input.ingredients
        ? assignIngredientIds(input.ingredients)
        : input.ingredients;
    return {
        ...input,
        version: 5,
        ingredients,
        activeStageId: mapStageId(input.activeStageId),
    };
}

function assignIngredientIds(ingredients: IngredientV4[]): IngredientV5[] {
    const usedIds = new Set<string>();
    return ingredients.map((ing) => {
        let id = ing.name;
        if (usedIds.has(id)) {
            let suffix = 2;
            while (usedIds.has(`${ing.name}-${suffix}`)) suffix += 1;
            id = `${ing.name}-${suffix}`;
        }
        usedIds.add(id);
        return { ...ing, id };
    });
}

function mapStageId(stageId: AnimationDataV4['activeStageId']): StageIdV5 | undefined {
    if (stageId === undefined) return undefined;
    if (stageId === 'character' || stageId === 'prepare-style') return 'ingredients';
    return stageId;
}
